import React from 'react';
import { BarChart3, DollarSign } from 'lucide-react';
import { useDashboardMetrics } from '../../hooks/useDashboardMetrics';
import { StandardButton } from '../../components/standard';

interface CreditManagementDashboardProps {
  onNavigate: (view: string) => void;
  userRole?: string;
  onViewManagerDashboard?: () => void;
}

const CreditManagementDashboard: React.FC<CreditManagementDashboardProps> = ({ onNavigate, userRole, onViewManagerDashboard }) => {
  const { data: metrics, isLoading } = useDashboardMetrics();

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white text-lg">💳</div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Credit Management Operations</h1>
              <p className="text-gray-600 dark:text-gray-400">Credit repair, ID theft recovery, and credit development workflow</p>
            </div>
          </div>
          {(userRole === 'manager' || userRole === 'cm_supervisor') && onViewManagerDashboard && (
            <StandardButton
              variant="secondary"
              leftIcon={<BarChart3 className="h-4 w-4" />}
              onClick={onViewManagerDashboard}
            >
              Manager Dashboard
            </StandardButton>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
            onClick={() => onNavigate('credit-repair')}
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white">🔧</div>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300">Credit Repair</h3>
            </div>
            <p className="text-blue-700 dark:text-blue-400 text-sm">Remove negative items and improve credit scores</p>
            <div className="mt-4 text-2xl font-bold text-blue-800 dark:text-blue-400">
              {isLoading ? '...' : metrics?.activeServices.creditRepair || 0} Active {metrics?.activeServices.creditRepair === 1 ? 'Case' : 'Cases'}
            </div>
          </div>

          <div
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-6 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            onClick={() => onNavigate('id-theft')}
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center text-white">🛡️</div>
              <h3 className="text-lg font-semibold text-red-900 dark:text-red-300">ID Theft Recovery</h3>
            </div>
            <p className="text-red-700 dark:text-red-400 text-sm">Remove fraudulent accounts and restore identity</p>
            <div className="mt-4 text-2xl font-bold text-red-800 dark:text-red-400">
              {isLoading ? '...' : metrics?.activeServices.idTheft || 0} Active {metrics?.activeServices.idTheft === 1 ? 'Case' : 'Cases'}
            </div>
          </div>

          <div
            className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
            onClick={() => onNavigate('credit-development')}
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center text-white">📈</div>
              <h3 className="text-lg font-semibold text-green-900 dark:text-green-300">Credit Development</h3>
            </div>
            <p className="text-green-700 dark:text-green-400 text-sm">Build credit from no credit or limited history</p>
            <div className="mt-4 text-2xl font-bold text-green-800 dark:text-green-400">
              {isLoading ? '...' : metrics?.activeServices.creditDevelopment || 0} Active {metrics?.activeServices.creditDevelopment === 1 ? 'Client' : 'Clients'}
            </div>
          </div>

          <div
            className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-6 cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
            onClick={() => onNavigate('debt-settlement')}
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center text-white">
                <DollarSign className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-300">Debt Settlement</h3>
            </div>
            <p className="text-purple-700 dark:text-purple-400 text-sm">Negotiate and reduce outstanding debt balances</p>
            <div className="mt-4 text-2xl font-bold text-purple-800 dark:text-purple-400">
              0 Active Cases
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditManagementDashboard;